import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@app/hooks/useAuth';

/** Staff sign-in: password, then a code from the authenticator app (2026-10-29). */
export function LoginPage() {
  const { login, verifyCode, cancelMfa, mfaToken, loading, error } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const navigate = useNavigate();

  const onPassword = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await login(email, password);
      setPassword('');
    } catch {
      // error is already in store; UI shows it below
    }
  };

  const onCode = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await verifyCode(code);
      navigate('/', { replace: true });
    } catch {
      setCode('');
    }
  };

  const startAgain = () => {
    cancelMfa();
    setCode('');
  };

  const expired = Boolean(error && /expired/i.test(error));

  return (
    <div className="mx-auto mt-20 max-w-sm rounded border bg-white p-6 shadow">
      <h1 className="mb-5 flex flex-col items-center gap-1">
        <img src="/talky-logo.png" alt="Talky" width={127} height={96} className="h-14 w-auto" />
        <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Admin</span>
      </h1>

      {mfaToken ? (
        <form key="code" onSubmit={onCode} className="space-y-3">
          <label className="block text-sm">
            Authenticator code
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              autoFocus
              autoComplete="one-time-code"
              placeholder="123456"
              className="mt-1 block w-full rounded border px-2 py-1 tracking-widest"
            />
          </label>
          <p className="text-xs text-slate-500">
            Open your authenticator app and enter the 6-digit code for Talky Admin. Lost your phone? Enter one of
            your recovery codes instead.
          </p>
          {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
          {expired ? (
            <button type="button" onClick={startAgain} className="w-full rounded bg-blue-600 py-2 text-white">
              Start again
            </button>
          ) : (
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded bg-blue-600 py-2 text-white disabled:opacity-50"
            >
              {loading ? 'Checking…' : 'Verify'}
            </button>
          )}
          <button type="button" onClick={startAgain} className="w-full text-sm text-slate-600 hover:underline">
            Back
          </button>
        </form>
      ) : (
        <form key="password" onSubmit={onPassword} className="space-y-3">
          <label className="block text-sm">
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              autoComplete="username"
              className="mt-1 block w-full rounded border px-2 py-1"
            />
          </label>
          <label className="block text-sm">
            Password
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="mt-1 block w-full rounded border px-2 py-1"
            />
          </label>
          {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full rounded bg-blue-600 py-2 text-white disabled:opacity-50"
          >
            {loading ? 'Signing in…' : 'Continue'}
          </button>
        </form>
      )}
    </div>
  );
}
